import { TableSqlBuilderHelper } from 'src/app/components/table-dashboard-item/sql-builder-helper';
import {
    IQueryContext,
    IQueryContextDatasourceColumn,
} from 'src/app/services/query-context/models/IQueryContext';
import { GcpProjectId } from '../gcp-project-id.model';
import { buildSplitGroupSQL, buildWhereSQL } from '../query/sql';
import { sqlDatetimeTrunc, sqlDateTrunc } from '../sql';
import {
    DatasourceColumnId,
    ICompiledSQLTokens,
    IDatasourceSQLMap,
    ISQLStatementsCompiler,
    mergeSqlTokensArray,
} from './datasource';

/**
 *  This class compiles SQL statements specific to a datasource, including handling of specific columns and split groups.
 */
export class DatasourceSpecificColumnsSQLStatementsCompiler implements ISQLStatementsCompiler {
    constructor(
        private options: {
            sqlMap: IDatasourceSQLMap;
            specificColumns: DatasourceColumnId[];
            /**
             * Prefix used to generate split group alias
             */
            datasourcePrefix: string;
        }
    ) {}

    public compileSQLStatements(
        supportedDatasourceColumns: IQueryContextDatasourceColumn[],
        queryContextSettings: IQueryContext['settings'],
        options: { gcpProjectId: GcpProjectId },
        customCompiledSqlTokens?: Partial<ICompiledSQLTokens>
    ) {
        // compile sql statements specific for this datasource
        const dateTrunc = (alias: string = 'date') =>
            queryContextSettings.dateAggregation
                ? sqlDateTrunc(alias, queryContextSettings.dateAggregation)
                : alias;

        const datetimeTrunc = (column?: string) => {
            column =
                column ??
                this.options.sqlMap.columns?.ACTIVITY_DATETIME!.columnSQL;
            return queryContextSettings.datetimeAggregation
                ? sqlDatetimeTrunc(
                      column,
                      queryContextSettings.datetimeAggregation
                  )
                : column;
        };

        const datasourceSpecificColumns: DatasourceColumnId[] =
            this.options.specificColumns;

        // if adFlowSPecificColumns wtih split applied are present in QueryContext
        const is_in_context_split_datasource_specific_columns =
            supportedDatasourceColumns.findIndex(
                (dsCol) =>
                    datasourceSpecificColumns.includes(dsCol.id) &&
                    dsCol.state.isSplitApplied
            ) != -1;

        const sqlTokensArray: {
            id: DatasourceColumnId;
            compiledSqlTokens: Partial<ICompiledSQLTokens>;
        }[] = supportedDatasourceColumns.map((dsCol) => {
            return {
                id: dsCol.id,
                compiledSqlTokens: this.options.sqlMap.columns[dsCol.id]!
                    .compileSQLTokens!(dsCol.state, options),
            };
        });

        // A Group/Bucket column uses the same datasource-specific compilation path as Split. Compile
        // it with split enabled to obtain the expression (including complex expressions), but keep it
        // in a separate SQL slot so it does not get concatenated into `split_group`.
        const bucketDatasourceColumn = supportedDatasourceColumns.find(
            (column) => column.state.isBucketApplied
        );
        const bucketCompiledSqlTokens = bucketDatasourceColumn
            ? this.options.sqlMap.columns[bucketDatasourceColumn.id]!
                  .compileSQLTokens!(
                  {
                      ...bucketDatasourceColumn.state,
                      isSplitApplied: true,
                      isBucketApplied: false,
                  },
                  options
              )
            : undefined;
        const bucketSplitTokens = bucketCompiledSqlTokens?.split_group ?? [];

        // Merge any JOIN/WHERE/UNNEST dependencies required by the grouped expression into the
        // regular statements, while deliberately excluding its split token from `split_group`.
        const bucketDependencies: Partial<ICompiledSQLTokens> = {
            ...bucketCompiledSqlTokens,
            split_group: [],
        };
        const sqlTokens: ICompiledSQLTokens = mergeSqlTokensArray([
            mergeSqlTokensArray(sqlTokensArray),
            bucketDependencies,
        ]);

        // revenue specfic
        const sqlTokensRevenueSpecific: ICompiledSQLTokens =
            mergeSqlTokensArray(
                supportedDatasourceColumns
                    .filter((dsCol) =>
                        datasourceSpecificColumns.includes(dsCol.id)
                    )
                    .map((dsCol) => {
                        return this.options.sqlMap.columns[dsCol.id]!
                            .compileSQLTokens!(dsCol.state, options);
                    })
            );

        const DATASOURCE_SPECIFIC_SPLIT_GROUP =
            this.options.datasourcePrefix + '_split_group';

        const datasourceSpecificSplitSelect = buildSplitGroupSQL(
            sqlTokensRevenueSpecific.split_group,
            DATASOURCE_SPECIFIC_SPLIT_GROUP
        );
        const datasourceSpecificSplitGroupAlias = datasourceSpecificSplitSelect
            ? DATASOURCE_SPECIFIC_SPLIT_GROUP
            : '';
        // to samo ale z przecinkiem na koncu
        const datasourceSpecificSplitGroupBy = datasourceSpecificSplitSelect
            ? `${datasourceSpecificSplitGroupAlias}, `
            : '';

        // cohort - non revenue specfic
        const sqlTokensNonDatasourceSpecific: ICompiledSQLTokens =
            mergeSqlTokensArray(
                supportedDatasourceColumns
                    .filter(
                        (dsCol) => !datasourceSpecificColumns.includes(dsCol.id)
                    )
                    .map((dsCol) => {
                        return this.options.sqlMap.columns[dsCol.id]!
                            .compileSQLTokens!(dsCol.state, options);
                    })
            );

        const cohortSplitSelect = buildSplitGroupSQL(
            sqlTokensNonDatasourceSpecific.split_group,
            'cohort_split_group'
        );
        const cohortSplitGroupAlias = cohortSplitSelect
            ? 'cohort_split_group'
            : '';
        const cohortSplitGroupBy = cohortSplitSelect
            ? `${cohortSplitGroupAlias}, `
            : '';
        const cohortWhere = buildWhereSQL(sqlTokensNonDatasourceSpecific.where);

        const splitSelect: string = buildSplitGroupSQL(sqlTokens.split_group);
        const bucketSelect: string = buildSplitGroupSQL(
            bucketSplitTokens,
            'bucket_group',
            false
        );
        const bucketGroup: string = bucketSelect ? 'bucket_group' : '';
        const bucketGroupBy = bucketSelect ? 'bucket_group, ' : '';

        const splitSelectWithMeta: {
            id: DatasourceColumnId;
            compiledSqlTokens: Partial<ICompiledSQLTokens>;
        }[] = supportedDatasourceColumns.map((dsCol) => {
            return {
                id: dsCol.id,
                compiledSqlTokens: this.options.sqlMap.columns[dsCol.id]!
                    .compileSQLTokens!(dsCol.state, options),
            };
        });

        const splitSelectAsSeparateColumns =
            TableSqlBuilderHelper.getSplitSelect(splitSelectWithMeta);
        const splitGroup: string = splitSelect ? 'split_group' : '';
        const splitGroupBy = splitSelect ? 'split_group, ' : '';

        const where: string = buildWhereSQL([
            ...sqlTokens.where,
            ...(customCompiledSqlTokens?.where ?? []),
        ]);
        const join: string = sqlTokens.join.join('\n');
        const windowTotal = splitGroupBy ? 'PARTITION BY split_group' : '';
        const windowRunning = (prefix: string = '') => {
            prefix = prefix ? `${prefix}.` : '';
            return splitGroupBy
                ? `PARTITION BY ${prefix}split_group ORDER BY ${prefix}day_no`
                : `ORDER BY ${prefix}day_no`;
        };

        /**
         * Constructs a SQL split group clause by concatenating the cohort and revenue split group aliases.
         *
         * This function first checks if both cohort and revenue split group aliases are present. If they are, it concatenates them with a comma separator and returns the result as 'split_group'.
         * If only one of them is present, it returns that one as 'split_group'.
         * If neither of them is present, it returns an empty string.
         *
         * @returns {string} - A SQL split group clause, or an empty string if neither cohort nor revenue split group alias is present.
         *
         * @example
         * // Assuming cohortSplitGroupAlias = "cohort_alias" and revenueSplitGroupAlias = "revenue_alias"
         * const splitGroupClause = splitSelectConcatCohortAdsFlow();
         * // Outputs: "CONCAT(cohort_alias, ', ', revenue_alias) AS split_group,"
         */
        const splitSelectConcatCohortAndDatasourceSpecificColumn = (() => {
            const areBothPresent =
                cohortSplitGroupBy != '' &&
                datasourceSpecificSplitGroupBy != '';

            if (areBothPresent) {
                return `CONCAT(${cohortSplitGroupAlias}, ', ', ${datasourceSpecificSplitGroupAlias} ) AS split_group,`;
            }
            // if any of them is present then return it
            if (
                datasourceSpecificSplitGroupBy != '' ||
                cohortSplitGroupBy != ''
            ) {
                // remove trailing comma
                return `${datasourceSpecificSplitGroupAlias}${cohortSplitGroupAlias} AS split_group,`;
            }
            return '';
        })();

        return {
            datetimeTrunc,
            dateTrunc,
            splitSelect,
            /** Group/Bucket expression compiled through the datasource map, aliased as `bucket_group`. */
            bucketSelect,
            /** `bucket_group` or empty string. */
            bucketGroup,
            /** `bucket_group, ` or empty string. */
            bucketGroupBy,
            /** splitSelect in the form: dsCol.columnSQL AS dsCol.id **/
            splitSelectAsSeparateColumns,
            /** `split_group` or empty string */
            splitGroup,
            /** `split_group,` (NOTICE comma at the end) or empty string */
            splitGroupBy,
            /** `REVENUE_COHORT_ALIASES AS split_group` or empty string */
            splitSelectConcatCohortAndDatasourceSpecificColumn,
            /** `REVENUE_SPECIFIC_SPLITS AS revenue_split_group`  or empty string */
            datasourceSpecificSplitSelect,
            /** `revenue_split_group` or empty string */
            datasourceSpecificSplitGroupAlias,
            /** `revenue_split_group,` (NOTICE comma at the end) or empty string */
            datasourceSpecificSplitGroupBy,
            /** `COHORT_SPECIFIC_SPLITS AS cohort_split_group` or empty string */
            cohortSplitSelect,
            /** `cohort_split_group` or empty string */
            cohortSplitGroupAlias,
            /** `cohort_split_group,` (NOTICE comma at the end) or empty string */
            cohortSplitGroupBy,
            /** Where statements with only cohort specific columns */
            cohortWhere,
            is_in_context_split_datasource_specific_columns,
            where,
            windowTotal,
            /** `PARTITION BY split_group  ORDER BY day_no` or `ORDER BY day_no` */
            windowRunning,
            join,
        };
    }
}
